import React from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import AuthGuard from './guards/AuthGuard';
import MenuBasedRouteGuard from './guards/MenuBasedRouteGuard';
import MainLayout from './layouts/MainLayout';
import Loading from './components/Loading';
import { useAuth } from './contexts/AuthProvider';
import { useUserPermissionAndMenu } from './contexts/UserPermissionAndMenuProvider';
import UserManagement from './pages/UserManagement';
import ItemManagement from './pages/ItemManagement';
import ItemGroupManagement from './pages/ItemGroupManagement';
import BOMManagement from './pages/BOMManagement';
import ItemResourceManagement from './pages/ItemResourceManagement';
import ItemResourceApprovalPanel from './pages/ItemResourceApprovalPanel';
import GLAccountManagement from './pages/GLAccountManagement';
import CustomerManagement from './pages/CustomerManagement';
import VendorManagement from './pages/VendorManagement';
import PromotionManagement from './pages/PromotionManagement';
import PromotionApprovalPanel from './pages/PromotionApprovalPanel';
import NotFoundPage from './pages/NotFoundPage';
import NoAccessPage from './pages/NoAccessPage';
import SignOn from './pages/Signon';
import SignOff from './pages/Signoff';
import CashReceipt from './pages/CashReceipt';
import BillingScreen from './pages/BillingScreen';
import TransactionScreen from './pages/TransactionScreen';
import Accounts from './pages/Accounts';
import OtherPayments from './pages/OtherPayments';
import Reports from './pages/Reports';
import Report from './pages/Report';
import CashierSignOnGuard from './guards/CashierSignOnGuard';

// Route element wrapped in the auth check and the it_user_menu grant check for
// `menuPath` (the it_system_menu.to_url that grants it).
const guarded = (menuPath: string, element: React.ReactNode) => (
    <AuthGuard>
        <MenuBasedRouteGuard requiredMenuPath={menuPath}>{element}</MenuBasedRouteGuard>
    </AuthGuard>
);

// "/" has no menu of its own - send the user to the first page they can open,
// or to NoAccessPage when the account has no usable menu grants at all.
const HomeRedirect = () => {
    const { menusSettled, firstAccessibleRoute } = useUserPermissionAndMenu();
    if (!menusSettled) {
        return <Loading message="Loading..." />;
    }
    const target = firstAccessibleRoute();
    return target ? <Navigate to={target} replace /> : <NoAccessPage />;
};

const DynamicRoutes = () => {
    const location = useLocation();
    const billingRoute = <AuthGuard><CashierSignOnGuard><BillingScreen /></CashierSignOnGuard></AuthGuard>;

    return (
        <Routes key={location.pathname}>
            <Route path="/" element={<AuthGuard><HomeRedirect /></AuthGuard>} />
            <Route path="/users" element={guarded('/users', <UserManagement />)} />
            <Route path="/items" element={guarded('/items', <ItemManagement />)} />
            <Route path="/item-groups" element={guarded('/item-groups', <ItemGroupManagement />)} />
            <Route path="/bom-management" element={guarded('/bom-management', <BOMManagement />)} />
            <Route path="/item-resource-management" element={guarded('/item-resource-management', <ItemResourceManagement />)} />
            <Route path="/item-resource-approval" element={guarded('/item-resource-approval', <ItemResourceApprovalPanel />)} />
            <Route path="/promotions" element={guarded('/promotions', <PromotionManagement />)} />
            <Route path="/promotion-approval" element={guarded('/promotion-approval', <PromotionApprovalPanel />)} />
            <Route path="/gl-accounts" element={guarded('/gl-accounts', <GLAccountManagement />)} />
            <Route path="/customers" element={guarded('/customers', <CustomerManagement />)} />
            <Route path="/vendors" element={guarded('/vendors', <VendorManagement />)} />
            <Route path="/cashier-sign-on" element={guarded('/cashier-sign-on', <SignOn />)} />
            <Route path="/cashier-sign-off" element={guarded('/cashier-sign-off', <SignOff />)} />
            <Route path="/cash-receipt" element={guarded('/cash-receipt', <CashReceipt />)} />
            <Route path="/accounts" element={guarded('/accounts', <Accounts />)} />
            <Route path="/accounts/other-payments" element={guarded('/accounts/other-payments', <OtherPayments />)} />
            <Route path="/billing" element={billingRoute} />
            <Route path="/transaction" element={guarded('/transaction', <TransactionScreen />)} />
            <Route path="/reports" element={guarded('/reports', <Reports />)} />
            <Route path="/reports/view/:reportTemplateId" element={guarded('/reports', <Report />)} />
            <Route path="*" element={<NotFoundPage />} />
        </Routes>
    );
};

const ProtectedApp = () => {
    return (
        <AuthGuard>
            <MainLayout>
                <DynamicRoutes />
            </MainLayout>
        </AuthGuard>
    );
};

const AppRoutes = () => {
    const { isAuthenticated, loading: authLoading } = useAuth();

    // Show loading while checking auth
    if (authLoading) {
        return <Loading message="Initializing..." />;
    }

    // Render based on authentication state
    return (
        <Routes>
            <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />} />
            <Route path="*" element={isAuthenticated ? <ProtectedApp /> : <Navigate to="/login" replace />} />
        </Routes>
    );
};

export default AppRoutes;
